/**
 * Scored fuzzy match: returns a non-negative penalty score (lower = better match)
 * or -1 if all characters of `query` are not found in sequence within `target`.
 *
 * The penalty is the total "gap" between consecutive matched characters, so
 * consecutive matches (e.g. exact prefix) score 1, while spread-out matches
 * score higher.
 */
export function fuzzyScore(query: string, target: string): number {
  const q = query.toLowerCase();
  const t = target.toLowerCase();
  let qi = 0;
  let lastMatchIdx = -1;
  let score = 0;

  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      if (lastMatchIdx >= 0) score += ti - lastMatchIdx - 1;
      lastMatchIdx = ti;
      qi++;
    }
  }

  return qi === q.length ? score + 1 : -1;
}

/**
 * Best score across multiple fields; returns the lowest non-negative score
 * across all fields, or -1 if no field matched.
 */
export function fuzzyScoreFields(query: string, fields: (string | null | undefined)[]): number {
  let best = -1;
  for (const field of fields) {
    if (!field) continue;
    const s = fuzzyScore(query, field);
    if (s >= 0 && (best < 0 || s < best)) best = s;
  }
  return best;
}

/**
 * Fuzzy-search a list, scoring each item against multiple fields.
 * When `query` is empty the original array is returned unchanged.
 * Results are sorted best-match-first (lowest score first).
 */
export function fuzzySearchList<T>(
  list: T[],
  query: string,
  getFields: (item: T) => (string | null | undefined)[],
): T[] {
  if (!query.trim()) return list;

  const scored = list
    .map((item) => ({ item, score: fuzzyScoreFields(query.trim(), getFields(item)) }))
    .filter((e) => e.score >= 0)
    .sort((a, b) => a.score - b.score);

  return scored.map((e) => e.item);
}
